import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, InputNumber, Modal, Select } from "antd";
import { saveAs } from "file-saver";
import { Redirect } from "react-router-dom";
import Header from "../../CoreComponents/Header";
import MainHeader from "../../CoreComponents/MainHeader";
import NavBar from "../../CoreComponents/NavBar";
import "./settlements.css";
import { showMessage } from "../../Functionalities/Functions";
import { username } from "../../Functionalities/Config";
import { fmtDate, rupees, rupeesWhole } from "../Insights/format";
import { loadPayoutOptions, savePayout } from "../Payouts/Logic";
import { todayInIndia } from "../Payouts/PayoutFormModal";
import { canSettle } from "./access";
import DayBasisSwitch from "../../CoreComponents/DayBasisSwitch";
import { DAY_COMPLETED, useRefetchOnBasis } from "../../Functionalities/dayBasis";
import { fetchPeriodPayouts, fetchRestaurantSettlement, payoutPayload, payoutPrefill, presets, restaurantCsv } from "./restaurantLogic";

const PRESET_LABELS = { today: "Today", yesterday: "Yesterday", last7: "Last 7 days", thisMonth: "This month" };

const REQUIRED = [{ required: true, message: "Required" }];

export default function RestaurantSettlement() {
  const [range, setRange] = useState(() => presets().yesterday);
  const [summary, setSummary] = useState(null);
  const [basis, setBasis] = useState(DAY_COMPLETED);
  const [options, setOptions] = useState({ transaction_types: [], transaction_modes: [] });
  const [recording, setRecording] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [payouts, setPayouts] = useState([]);
  const [form] = Form.useForm();
  const [showDays, setShowDays] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPayoutOptions().then((body) => {
      if (body && body.Status === true) {
        setOptions({ transaction_types: body.transaction_types || [], transaction_modes: body.transaction_modes || [] });
      }
    });
  }, []);

  const visible = useMemo(() => {
    const list = summary ? summary.restaurants : [];
    const query = search.trim().toLowerCase();
    if (!query) return list;
    return list.filter((r) => String(r.id).includes(query) || String(r.name).toLowerCase().includes(query));
  }, [summary, search]);

  useRefetchOnBasis(basis, () => summary && load());

  if (!canSettle()) return <Redirect to="/orders" />;

  const load = async () => {
    if (!range.from || !range.to) {
      showMessage("Pick both dates", "warning", "Dates", "top", "top-right");
      return;
    }
    setLoading(true);
    const body = await fetchRestaurantSettlement(range.from, range.to, basis);
    setLoading(false);
    if (!body || body.Status !== true) {
      setSummary(null);
      showMessage((body && body.Error) || "Could not load the settlement", "danger", "Error!", "top", "top-right");
      return;
    }
    setSummary(body);
    setPayouts(await fetchPeriodPayouts(range.from, range.to));
    showMessage(`${body.orders} completed orders between ${range.from} and ${range.to}`, "info", "Loaded", "top", "top-right");
  };

  const openRecord = (restaurant) => {
    setFormError("");
    form.setFieldsValue(payoutPrefill(restaurant, { from: range.from, to: range.to, today: todayInIndia(), doneBy: username }));
    setRecording(restaurant);
  };

  const closeRecord = () => {
    setRecording(null);
    form.resetFields();
  };

  const record = async (values) => {
    setSaving(true);
    setFormError("");
    const body = await savePayout(null, payoutPayload(recording.id, values));
    setSaving(false);
    if (!body || body.Status !== true) {
      setFormError((body && body.Error) || "Could not save the settlement");
      return;
    }
    showMessage(`${recording.name}: ${rupees(values.amount)} recorded`, "success", "Settlement saved", "top", "top-right");
    closeRecord();
    setPayouts(await fetchPeriodPayouts(range.from, range.to));
  };

  const download = () =>
    saveAs(new Blob([restaurantCsv(summary.restaurants)], { type: "text/csv;charset=utf-8" }), `restaurant-settlement-${range.from}-${range.to}.csv`);

  const kpi = (label, value, tone) => (
    <div className="col-6 col-md-4 col-xl mb-3" key={label}>
      <div className="card mb-0 h-100">
        <div className="card-body py-3">
          <div className="text-muted" style={{ fontSize: 12, textTransform: "uppercase" }}>{label}</div>
          <div className={`font-w700 ${tone || ""}`} style={{ fontSize: 22 }}>{value}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div id="main-wrapper">
      <MainHeader />
      <Header name={"Restaurant settlement"} />
      <NavBar />
      <div className="content-body" style={{ minHeight: "100vh" }}>
        <div className="container-fluid settlements">
          <div className="card">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <h4 className="card-title mb-1">What each restaurant is owed for a period</h4>
                <p className="mb-0 text-muted">
                  Completed orders between two dates, the franchise's side of them, and a payout line per restaurant. Saved payouts appear under Restaurant Payouts and in the restaurant's app.
                </p>
              </div>
              <button type="button" className="btn btn-primary" disabled={!summary || summary.restaurants.length === 0} onClick={download}>
                Download CSV
              </button>
            </div>
            <div className="card-body">
              <div className="row align-items-end mb-2">
                <div className="col-md-3 mb-2">
                  <label className="text-black font-w500">From</label>
                  <input className="form-control" type="date" value={range.from} onChange={(e) => setRange((prev) => ({ ...prev, from: e.target.value }))} />
                </div>
                <div className="col-md-3 mb-2">
                  <label className="text-black font-w500">To</label>
                  <input className="form-control" type="date" value={range.to} onChange={(e) => setRange((prev) => ({ ...prev, to: e.target.value }))} />
                </div>
                <div className="col-md-3 mb-2">
                  <DayBasisSwitch value={basis} onChange={setBasis} />
                </div>
                <div className="col-md-3 mb-2 d-flex flex-wrap align-items-center" style={{ gap: 6 }}>
                  {Object.entries(PRESET_LABELS).map(([key, label]) => (
                    <button key={key} type="button" className="btn btn-xs btn-light" onClick={() => setRange(presets()[key])}>
                      {label}
                    </button>
                  ))}
                  <button type="button" className="btn btn-outline-primary ml-2" disabled={loading} onClick={load}>
                    {loading ? "Loading…" : "Get details"}
                  </button>
                </div>
              </div>

              {summary && summary.orders > 0 && (
                <div className="row">
                  {kpi("Gross sales", rupeesWhole(summary.totals.sales))}
                  {kpi("Franchise commission", rupees(summary.totals.commission), "text-primary")}
                  {kpi("Store charges", rupees(summary.totals.store_charge))}
                  {kpi("Branch GST", rupees(summary.totals.gst))}
                  {kpi(`Owed to ${summary.restaurants.length} restaurants`, rupeesWhole(summary.owed_total), "text-danger")}
                </div>
              )}

              {summary && summary.by_day.length > 0 && (
                <div className="mb-3">
                  <button type="button" className="btn btn-xs btn-outline-secondary" onClick={() => setShowDays((v) => !v)}>
                    {showDays ? "Hide the day-by-day table" : `Day by day (${summary.by_day.length} days)`}
                  </button>
                  {showDays && (
                    <div className="table-responsive mt-2">
                      <table className="table table-sm dataTablesCard">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th className="text-right">Sales</th>
                            <th className="text-right">Delivery charge</th>
                            <th className="text-right">Store charge</th>
                            <th className="text-right">Commission</th>
                            <th className="text-right">Extra commission</th>
                            <th className="text-right">Branch GST</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.by_day.map((day) => (
                            <tr key={day.date}>
                              <td>{day.date}</td>
                              <td className="text-right">{rupeesWhole(day.sales)}</td>
                              <td className="text-right">{rupees(day.delivery_charge)}</td>
                              <td className="text-right">{rupees(day.store_charge)}</td>
                              <td className="text-right font-w600">{rupees(day.commission)}</td>
                              <td className="text-right">{rupees(day.extra_commission)}</td>
                              <td className="text-right">{rupees(day.gst)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-muted">
                  {!summary ? "Pick the dates and fetch the orders." : `${visible.length} of ${summary.restaurants.length} restaurants`}
                </span>
                <input className="form-control" style={{ maxWidth: 240 }} placeholder="Search restaurant or ID" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>

              <div className="table-responsive">
                <table className="table table-sm dataTablesCard">
                  <thead>
                    <tr>
                      <th>Restaurant</th>
                      <th className="text-center">Orders</th>
                      <th className="text-right">Owed</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          {!summary ? "Nothing loaded yet." : "No restaurant matches."}
                        </td>
                      </tr>
                    ) : (
                      visible.map((restaurant) => (
                        <tr key={restaurant.id}>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <span className="font-w600">{restaurant.name}</span>
                            <span className="text-muted"> #{restaurant.id}</span>
                          </td>
                          <td className="text-center">{restaurant.orders}</td>
                          <td className="text-right font-w600 text-danger">{rupeesWhole(restaurant.owed)}</td>
                          <td className="text-right">
                            <button type="button" className="btn btn-xs btn-primary" onClick={() => openRecord(restaurant)}>
                              Record settlement
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {summary && (
                <div className="mt-4">
                  <h5 className="mb-2">Settlements recorded for this period</h5>
                  <div className="table-responsive">
                    <table className="table table-sm dataTablesCard">
                      <thead>
                        <tr>
                          <th>Restaurant</th>
                          <th>Paid on</th>
                          <th>Period</th>
                          <th className="text-right">Amount</th>
                          <th className="text-right">Difference</th>
                          <th className="text-right">Pending</th>
                          <th>Mode</th>
                          <th>Transaction id</th>
                          <th>Done by</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payouts.length === 0 ? (
                          <tr>
                            <td colSpan={10} className="text-center py-3 text-muted">
                              No settlements recorded for these dates yet.
                            </td>
                          </tr>
                        ) : (
                          payouts.map((payout) => (
                            <tr key={payout.id}>
                              <td>{payout.branch_name}</td>
                              <td style={{ whiteSpace: "nowrap" }}>{fmtDate(payout.paid_on)}</td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {payout.period_from ? `${fmtDate(payout.period_from)} – ${fmtDate(payout.period_to || payout.period_from)}` : "—"}
                              </td>
                              <td className="text-right font-w600">{rupees(payout.amount)}</td>
                              <td className="text-right">{rupees(payout.difference)}</td>
                              <td className="text-right">{rupees(payout.pending_amount)}</td>
                              <td>{payout.transaction_mode_label || "—"}</td>
                              <td>{payout.transaction_id || "—"}</td>
                              <td>{payout.done_by || "—"}</td>
                              <td style={{ maxWidth: 220, whiteSpace: "pre-wrap" }}>{payout.notes || "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        open={Boolean(recording)}
        title={recording ? `Record settlement: ${recording.name}` : ""}
        rootClassName="settlements"
        onCancel={closeRecord}
        destroyOnClose
        footer={[
          <button key="cancel" type="button" className="btn btn-light mr-2" onClick={closeRecord}>
            Cancel
          </button>,
          <button key="save" type="button" className="btn btn-primary" disabled={saving} onClick={() => form.submit()}>
            {saving ? "Saving…" : "Save settlement"}
          </button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={record} preserve={false}>
          <div className="row">
            <Form.Item className="col-6" label="Period from" name="period_from" rules={REQUIRED}>
              <Input type="date" />
            </Form.Item>
            <Form.Item className="col-6" label="Period to" name="period_to" rules={REQUIRED}>
              <Input type="date" />
            </Form.Item>
          </div>
          <Form.Item
            label="Settlement amount (₹)"
            name="amount"
            rules={[{ required: true, message: "Required" }, { type: "number", min: 1, message: "Must be above 0" }]}
            extra="Food amount without extra commission − restaurant commission, for this period"
          >
            <InputNumber style={{ width: "100%" }} min={0} precision={0} />
          </Form.Item>
          <div className="row">
            <Form.Item className="col-6" label="Difference (₹, may be negative)" name="difference">
              <InputNumber style={{ width: "100%" }} precision={2} />
            </Form.Item>
            <Form.Item className="col-6" label="Pending amount (₹)" name="pending_amount">
              <InputNumber style={{ width: "100%" }} min={0} precision={2} />
            </Form.Item>
          </div>
          <div className="row">
            <Form.Item className="col-6" label="Transaction type" name="transaction_type" rules={REQUIRED}>
              <Select options={options.transaction_types} />
            </Form.Item>
            <Form.Item className="col-6" label="Transaction mode" name="transaction_mode">
              <Select options={options.transaction_modes} placeholder="Select" />
            </Form.Item>
          </div>
          <div className="row">
            <Form.Item className="col-6" label="Transaction id" name="transaction_id">
              <Input />
            </Form.Item>
            <Form.Item className="col-6" label="Done by" name="done_by">
              <Input maxLength={120} />
            </Form.Item>
          </div>
          <Form.Item label="Paid on" name="paid_on" rules={REQUIRED}>
            <Input type="date" />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          {formError && <div className="text-danger">{formError}</div>}
        </Form>
      </Modal>
    </div>
  );
}
